import React, { useState, useMemo, useEffect } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { BlankFiller } from './BlankFiller';
import { EducationalTooltip } from './EducationalTooltip';
import { MobileHelper } from './MobileHelper';
import { theme } from '../../styles/theme';
import { Button } from '../common/Button';
import { fossilFillsApi } from '../../services/fossilFillsApi';

interface BlankDefinition {
  id: string;
  type: string;
  label: string;
  placeholder: string;
  suggestions: string[];
  required: boolean;
  help_text: string;
  position: number;
  original_text?: string;
  removable?: boolean;
  removal_context?: string;
}

interface FossilTemplate {
  template: string;
  blanks: BlankDefinition[];
  domain: string;
  educational_notes: string[];
  original_prompt: string;
  basic_improvement: string;
}

interface Props {
  template: FossilTemplate;
  filledBlanks: Record<string, string>;
  onBlankFill: (blankId: string, value: string) => void;
  onComplete: () => void;
  onDomainChange?: (domain: string) => void;
  onStartOver?: () => void;
}

const Container = styled.div`
  background: ${theme.colors.surface};
  border-radius: ${theme.radii.lg};
  padding: ${theme.spacing['2xl']};
  box-shadow: ${theme.shadows.sm};
  
  @media (max-width: ${theme.breakpoints.md}) {
    padding: ${theme.spacing.lg};
    border-radius: ${theme.radii.md};
  }
  
  @media (max-width: ${theme.breakpoints.sm}) {
    padding: ${theme.spacing.md};
  }
`;

const DomainSection = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.md};
  margin-bottom: ${theme.spacing.lg};
  flex-wrap: wrap;
  position: sticky;
  top: 0;
  background: ${theme.colors.surface};
  padding: ${theme.spacing.md} 0;
  z-index: 10;
  border-bottom: 1px solid ${theme.colors.border};
`;

const DomainLabel = styled.span`
  font-size: ${theme.fontSizes.sm};
  color: ${theme.colors.text.secondary};
  font-weight: 500;
`;

const DomainSelect = styled.select`
  background: ${theme.colors.surface};
  color: ${theme.colors.text.primary};
  padding: ${theme.spacing.xs} ${theme.spacing.md};
  padding-right: ${theme.spacing.xl};
  border: 2px solid ${theme.colors.border};
  border-radius: ${theme.radii.md};
  font-size: ${theme.fontSizes.sm};
  font-weight: 600;
  text-transform: capitalize;
  cursor: pointer;
  transition: all 0.2s ease;
  appearance: none;
  background-image: url('data:image/svg+xml;utf8,<svg fill="%234B5563" height="20" width="20" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"/></svg>');
  background-repeat: no-repeat;
  background-position: right 0.5rem center;
  background-size: 1.5rem;
  
  &:hover {
    border-color: ${theme.colors.primary};
    background-color: ${theme.colors.backgroundSecondary};
  }
  
  &:focus {
    outline: none;
    border-color: ${theme.colors.primary};
    box-shadow: 0 0 0 3px ${theme.colors.primaryLight};
  }
`;

// DomainBadge removed - now using DomainSection with dropdown

const TemplateDisplay = styled.div`
  background: ${theme.colors.background};
  border: 2px solid ${theme.colors.border};
  border-radius: ${theme.radii.md};
  padding: ${theme.spacing.xl};
  font-size: ${theme.fontSizes.lg};
  line-height: 2;
  margin-bottom: ${theme.spacing.xl};
  position: relative;
  
  @media (max-width: ${theme.breakpoints.md}) {
    font-size: ${theme.fontSizes.base};
    padding: ${theme.spacing.lg};
    line-height: 1.8;
  }
  
  @media (max-width: ${theme.breakpoints.sm}) {
    font-size: ${theme.fontSizes.sm};
    padding: ${theme.spacing.md};
    line-height: 2.2;
    margin-bottom: ${theme.spacing.lg};
  }
`;

const TextPart = styled.span`
  color: ${theme.colors.text.primary};
`;

const BlankWrapper = styled.span`
  position: relative;
  display: inline-block;
  margin: 0 4px;
  
  @media (max-width: ${theme.breakpoints.sm}) {
    display: inline-flex;
    align-items: baseline;
    margin: 0 2px;
    max-width: 100%;
  }
`;

const ProgressBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${theme.spacing.xl};
  padding: ${theme.spacing.lg};
  background: ${theme.colors.backgroundSecondary};
  border-radius: ${theme.radii.md};
  
  @media (max-width: ${theme.breakpoints.sm}) {
    flex-direction: column;
    gap: ${theme.spacing.sm};
    padding: ${theme.spacing.md};
    margin-bottom: ${theme.spacing.lg};
  }
`;

const ProgressText = styled.div`
  font-size: ${theme.fontSizes.sm};
  color: ${theme.colors.text.secondary};
`;

const ProgressBarFill = styled.div<{ percent: number }>`
  height: 8px;
  background: ${theme.colors.border};
  border-radius: ${theme.radii.full};
  position: relative;
  flex: 1;
  margin: 0 ${theme.spacing.lg};
  overflow: hidden;
  
  &::after {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    height: 100%;
    width: ${props => props.percent}%;
    background: linear-gradient(90deg, ${theme.colors.primary}, ${theme.colors.primaryDark});
    border-radius: ${theme.radii.full};
    transition: width 0.3s ease;
  }
  
  @media (max-width: ${theme.breakpoints.sm}) {
    width: 100%;
    margin: 0;
  }
`;

const EducationalNotes = styled.div`
  background: linear-gradient(135deg, #f0f9ff, #e0f2fe);
  border: 1px solid #bae6fd;
  border-radius: ${theme.radii.md};
  padding: ${theme.spacing.xl};
  margin-top: ${theme.spacing.xl};
  
  @media (max-width: ${theme.breakpoints.sm}) {
    padding: ${theme.spacing.md};
    margin-top: ${theme.spacing.lg};
  }
`;

const NotesTitle = styled.h3`
  font-size: ${theme.fontSizes.lg};
  color: ${theme.colors.text.primary};
  margin-bottom: ${theme.spacing.md};
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
`;

const Note = styled.div`
  font-size: ${theme.fontSizes.base};
  color: ${theme.colors.text.secondary};
  line-height: 1.6;
  margin-bottom: ${theme.spacing.sm};
  padding-left: ${theme.spacing.lg};
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const ActionButtons = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: ${theme.spacing.xl};
  gap: ${theme.spacing.md};
  
  @media (max-width: ${theme.breakpoints.sm}) {
    flex-direction: column;
    align-items: stretch;
    margin-top: ${theme.spacing.lg};
  }
`;

const CompleteButton = styled(Button)`
  padding: ${theme.spacing.md} ${theme.spacing['2xl']};
  font-size: ${theme.fontSizes.base};
  font-weight: 600;
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  
  @media (max-width: ${theme.breakpoints.sm}) {
    width: 100%;
    padding: ${theme.spacing.md} ${theme.spacing.lg};
  }
`;

const AutoFillButton = styled.button`
  background: transparent;
  border: 1px solid ${theme.colors.border};
  color: ${theme.colors.text.secondary};
  padding: ${theme.spacing.sm} ${theme.spacing.lg};
  border-radius: ${theme.radii.md};
  font-size: ${theme.fontSizes.sm};
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: ${theme.colors.backgroundSecondary};
    color: ${theme.colors.text.primary};
    border-color: ${theme.colors.primary};
  }
  
  @media (max-width: ${theme.breakpoints.sm}) {
    width: 100%;
    padding: ${theme.spacing.md};
    order: 2;
  }
`;

export const TemplateBuilder: React.FC<Props> = ({
  template,
  filledBlanks,
  onBlankFill,
  onComplete,
  onDomainChange,
  onStartOver
}) => {
  const [activeBlank, setActiveBlank] = useState<string | null>(null);
  const [showTooltip, setShowTooltip] = useState<string | null>(null);
  const [selectedDomain, setSelectedDomain] = useState(template.domain);
  const [availableDomains, setAvailableDomains] = useState<string[]>([template.domain]);
  
  useEffect(() => {
    // Fetch available domains from API
    fossilFillsApi.getAvailableDomains().then(response => {
      setAvailableDomains(response.domains);
    }).catch(error => {
      console.error('Failed to fetch domains:', error);
      // Use fallback domains
      setAvailableDomains([
        'general', 'screenwriting', 'creative_writing', 'business', 
        'technical', 'academic', 'marketing', 'programming'
      ]);
    });
  }, []);
  
  const handleDomainChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newDomain = e.target.value;
    setSelectedDomain(newDomain);
    if (onDomainChange) {
      onDomainChange(newDomain);
    }
  };
  
  // Detect if blanks are part of comma-separated lists
  const detectRemovableBlanks = useMemo(() => {
    const templateText = template.template;
    const removableMap: Record<string, boolean> = {};
    
    // Find patterns like: {{blank1}}, {{blank2}}, and {{blank3}}
    // or: {{blank1}}, {{blank2}} and {{blank3}}
    const listPattern = /\{\{(\w+):[^}]+\}\}(?:\s*,\s*\{\{(\w+):[^}]+\}\})+(?:\s*(?:,\s*)?(?:and|or)\s*\{\{(\w+):[^}]+\}\})?/g;
    
    let match;
    while ((match = listPattern.exec(templateText)) !== null) {
      // Extract all blank IDs from the matched pattern
      const blankPattern = /\{\{(\w+):[^}]+\}\}/g;
      let blankMatch;
      while ((blankMatch = blankPattern.exec(match[0])) !== null) {
        removableMap[blankMatch[1]] = true;
      }
    }
    
    return removableMap;
  }, [template.template]);
  
  // Parse template and replace blanks with interactive components
  const renderedTemplate = useMemo(() => {
    const parts: any[] = [];
    let templateText = template.template;
    
    // First, handle removed blanks by removing them with their surrounding delimiters
    Object.entries(filledBlanks).forEach(([blankId, value]) => {
      if (value === '__REMOVED__' && detectRemovableBlanks[blankId]) {
        // Pattern to match the blank and its surrounding delimiters
        // Handles: ", {{blank}}", "{{blank}}, ", "{{blank}} and", ", and {{blank}}"
        const patterns = [
          new RegExp(`,\\s*\\{\\{${blankId}:[^}]+\\}\\}`, 'g'), // , {{blank}}
          new RegExp(`\\{\\{${blankId}:[^}]+\\}\\}\\s*,`, 'g'), // {{blank}},
          new RegExp(`,\\s*and\\s*\\{\\{${blankId}:[^}]+\\}\\}`, 'g'), // , and {{blank}}
          new RegExp(`\\{\\{${blankId}:[^}]+\\}\\}\\s*and\\s*`, 'g'), // {{blank}} and
          new RegExp(`,\\s*or\\s*\\{\\{${blankId}:[^}]+\\}\\}`, 'g'), // , or {{blank}}
          new RegExp(`\\{\\{${blankId}:[^}]+\\}\\}\\s*or\\s*`, 'g'), // {{blank}} or
        ];
        
        for (const pattern of patterns) {
          if (pattern.test(templateText)) {
            templateText = templateText.replace(pattern, '');
            break;
          }
        }
      }
    });
    
    let lastIndex = 0;
    // Regular expression to find blanks: {{id:value}} or {{value}} or {value:description}
    const blankRegex = /\{\{(\w+):([^}]+)\}\}|\{\{([^}:]+)\}\}|\{([^}:]+):([^}]+)\}/g;
    let match;
    
    while ((match = blankRegex.exec(templateText)) !== null) {
      // Add text before blank
      if (match.index > lastIndex) {
        parts.push({
          type: 'text',
          content: templateText.slice(lastIndex, match.index)
        });
      }
      
      // Add blank - handle different match patterns
      // match[1] and match[2] for {{id:value}}
      // match[3] for {{value}}
      // match[4] and match[5] for {value:description}
      const blankId = match[1] || match[3] || match[4];
      const blank = template.blanks.find(b => 
        b.id === blankId || 
        b.type === blankId || 
        b.placeholder === blankId
      );
      
      if (blank) {
        // Check if this blank is marked as removed
        const isRemoved = filledBlanks[blank.id] === '__REMOVED__';
        if (!isRemoved) {
          // Add removable flag based on detection
          const enhancedBlank = {
            ...blank,
            removable: detectRemovableBlanks[blank.id] || false
          };
          parts.push({
            type: 'blank',
            blank: enhancedBlank,
            filled: filledBlanks[blank.id] || null
          });
        }
        // If removed, we skip adding the blank entirely
      } else {
        // If no matching blank found, treat as text with brackets
        // This handles any unmatched bracket patterns
        parts.push({
          type: 'text',
          content: match[0]
        });
      }
      
      lastIndex = match.index + match[0].length;
    }
    
    // Add remaining text
    if (lastIndex < templateText.length) {
      parts.push({
        type: 'text',
        content: templateText.slice(lastIndex)
      });
    }
    
    return parts;
  }, [template, filledBlanks, detectRemovableBlanks]);

  const filledCount = Object.keys(filledBlanks).filter(
    id => filledBlanks[id] && filledBlanks[id].trim() !== '' && filledBlanks[id] !== '__REMOVED__'
  ).length;
  
  const removedCount = Object.keys(filledBlanks).filter(
    id => filledBlanks[id] === '__REMOVED__'
  ).length;
  
  const totalRequired = template.blanks.filter(b => b.required && filledBlanks[b.id] !== '__REMOVED__').length;
  const progressPercent = totalRequired > 0 ? (filledCount / totalRequired) * 100 : 0;
  
  const allRequiredFilled = template.blanks.every(
    blank => !blank.required || filledBlanks[blank.id] === '__REMOVED__' || (filledBlanks[blank.id] && filledBlanks[blank.id].trim() !== '')
  );

  const handleAutoFill = () => {
    template.blanks.forEach(blank => {
      if (!filledBlanks[blank.id] || filledBlanks[blank.id].trim() === '') {
        // Use first suggestion or original text
        const value = blank.suggestions[0] || blank.original_text || '';
        if (value && value !== 'Custom...') {
          onBlankFill(blank.id, value);
        }
      }
    });
  };

  return (
    <Container>
      <DomainSection>
        <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing.md, flex: 1 }}>
          <DomainLabel>Detected Domain:</DomainLabel>
          <DomainSelect value={selectedDomain} onChange={handleDomainChange}>
            {availableDomains.map(domain => (
              <option key={domain} value={domain}>
                {domain.replace('_', ' ')} Domain
              </option>
            ))}
          </DomainSelect>
          {selectedDomain !== template.domain && (
            <motion.span
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              style={{ 
                fontSize: theme.fontSizes.xs, 
                color: theme.colors.text.muted,
                fontStyle: 'italic'
              }}
            >
              (originally: {template.domain})
            </motion.span>
          )}
        </div>
        {onStartOver && (
          <Button 
            variant="secondary"
            size="small"
            onClick={onStartOver}
            style={{ marginLeft: 'auto' }}
          >
            ← Start Over
          </Button>
        )}
      </DomainSection>
      
      <MobileHelper stage="filling" />
      
      <ProgressBar>
        <ProgressText>
          Progress: {filledCount} of {totalRequired} blanks filled
          {removedCount > 0 && ` (${removedCount} removed)`}
        </ProgressText>
        <ProgressBarFill percent={progressPercent} />
        <ProgressText>{Math.round(progressPercent)}%</ProgressText>
      </ProgressBar>
      
      <TemplateDisplay>
        {renderedTemplate.map((part, index) => {
          if (part.type === 'text') {
            return <TextPart key={index}>{part.content}</TextPart>;
          } else {
            const blank = part.blank;
            return (
              <BlankWrapper 
                key={blank.id}
                onMouseEnter={() => setShowTooltip(blank.id)}
                onMouseLeave={() => setShowTooltip(null)}
              >
                <BlankFiller
                  blank={{
                    ...blank,
                    removable: detectRemovableBlanks[blank.id] || false
                  }}
                  value={filledBlanks[blank.id]}
                  isActive={activeBlank === blank.id}
                  onFocus={() => setActiveBlank(blank.id)}
                  onBlur={() => setActiveBlank(null)}
                  onChange={(value) => onBlankFill(blank.id, value)}
                />
                <AnimatePresence>
                  {showTooltip === blank.id && (
                    <EducationalTooltip text={blank.help_text} />
                  )}
                </AnimatePresence>
              </BlankWrapper>
            );
          }
        })}
      </TemplateDisplay>

      <EducationalNotes>
        <NotesTitle>
          <span>💡</span>
          Why This Structure Works
        </NotesTitle>
        {template.educational_notes.map((note, index) => (
          <Note key={index}>{note}</Note>
        ))}
      </EducationalNotes>

      <ActionButtons>
        <AutoFillButton onClick={handleAutoFill}>
          Auto-fill with suggestions
        </AutoFillButton>
        <CompleteButton
          onClick={onComplete}
          disabled={!allRequiredFilled}
        >
          View Complete Prompt →
        </CompleteButton>
      </ActionButtons>
    </Container>
  );
};